import { useEffect } from "react";

/**
 * Asks before the tab is closed, reloaded or pointed elsewhere while
 * something is still on its way up.
 *
 * The phone never loses a frame it was handed: its uploads queue on disk
 * and go up whenever the app is next alive. A browser tab has no such
 * afterlife — closing it mid-upload drops the photograph, and closing it
 * mid-armful drops every photograph after the one in flight, with nothing
 * left to say so. What a page can do is ask, and the browser asks in its
 * own words: neither the sentence nor its buttons are ours to write, which
 * is why nothing here is translated.
 *
 * Only while something is in flight. A listener that is always attached
 * would put the question to somebody leaving a page that owes them nothing,
 * and that is the quickest way to teach people to answer it without
 * reading.
 */
export function useLeaveGuard(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const ask = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // What Chrome before 119 and Safari read instead of the cancel.
      event.returnValue = true;
    };
    window.addEventListener("beforeunload", ask);
    return () => window.removeEventListener("beforeunload", ask);
  }, [active]);
}
